import { Bookmark, Close, BookmarkBorderOutlined } from '@mui/icons-material';
import { Button, IconButton, Typography, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';

import { ReactComponent as ContentCopyIcon } from '../icons/content_copy_filled.svg';
import { DataGridPro, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';

const MACRO_FILTER_LIST = ['properties', 'description', 'type', 'format', 'items'];

function getKeysAndMacros(schema) {
    var leafNodes=[];
    iterateSchema(schema.properties, '');
    function iterateSchema(node, path) {
        if (Object(node) === node) {
            const { items, description, examples, format, properties } = node;
            if (items) {
                if (items.properties) {
                    for (const [key, value] of Object.entries(items.properties)) {
                        const {type, properties} = value;
                        if (type != 'object') {
                            pushLeafNode(path, key, value.description, value.examples);
                        } else if (properties) {
                            deepRecursion(path, key, properties);
                        }
                    }
                } else {
                    var keys = Object.keys(items);
                    for (const key of keys) {
                        pushLeafNode(path, key, description, examples);
                    }
                }
            } else if (!properties && description && (typeof description === 'string' || description instanceof String)) {
                if ((leafNodes.length == 0) || !leafNodes.some(e => e.path === path)) {
                    var appendSafe = (format=='html' ? '|safe' : '');
                    leafNodes.push({
                        path: `${path}`, 
                        code: `{{${path}${appendSafe}}}`,
                        description: `${description}`,
                        examples: `${examples != undefined ? examples : ''}`,
                    });
                }
            }
            for (const [key, value] of Object.entries(node)) {
                if (key === 'oneOf') {
                    for (var i=0; i<value.length; i++) {
                        iterateSchema(value[i], path);
                    }
                } else {
                    if (key !== 'description' && typeof value === 'object' && value !== null) {
                        iterateSchema(value, MACRO_FILTER_LIST.includes(key) ? path : (path ? path + '.' + key : key));
                    }
                }
            }    
        }

        function pushLeafNode(path, key, description, examples) {
            leafNodes.push({
                path: `${path}.${key}`,
                code: `{% for item in ${path} %} {{${key}}} {% endfor %}`,
                description: `${description}`,
                examples: `${examples != undefined ? examples : ''}`,
            });
        }

        function deepRecursion(base, parent, properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (value.type != 'object') {
                    pushLeafNode(`${base}.${parent}`, key, value.description, value.examples);
                }
            }
        };
    }
    return leafNodes;
}

function convertPathToHeirarchy(path) {
    var replacedLast = path.split('.');
    var leafNode = replacedLast.pop();
    replacedLast.push('{{' + leafNode + '}}');
    return replacedLast;
}

function getLeafNodes(schema, bookmarkedItems) {
    var leafNodes = [];
    var count = 0;
    var keysAndMacros = getKeysAndMacros(schema);
    keysAndMacros.forEach(function (item) {
        const {path, description, examples, code} = item;
        if (path) {
            var heirarchy = convertPathToHeirarchy(path);
            leafNodes.push({
                id: count++, 
                hierarchy: heirarchy, 
                path: path, 
                description: description,
                examples: examples ? 'E.g. ' + examples : '',
                code: code, 
                isLeafNode: true, 
                bookmark: Object.hasOwn(bookmarkedItems, path)});
        }
    });
    return leafNodes;
}

const getTreeDataPath = (row) => row.hierarchy;

const NoColumnHeaders = React.forwardRef((props, ref) => null);

export default function SchemaUI({ schema, handleCopyTagTemplate, toggleTagDictionary, loading }) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [bookmarkedItems, setBookmarkedItems] = React.useState(() => {
        const initialVal = JSON.parse(localStorage?.getItem('bookmarkedItems'));
        return initialVal || [];
    });

    React.useEffect(()=>{
        localStorage.setItem('bookmarkedItems', JSON.stringify(bookmarkedItems));
    }, [bookmarkedItems]);

    const [filterModel, setFilterModel] = React.useState({
        items: [],
        quickFilterValues: [],
    });

    const { enqueueSnackbar } = useSnackbar();

    const [rows, setRows] = React.useState(getLeafNodes(schema, bookmarkedItems));

    const columns = [
        { field: 'description' },
        { field: 'examples' },
        { field: 'details', headerName: 'Description', width: isSmallScreen ? 160 : 250,
            renderCell: ({value}) => {
                const {description, examples} = value;
                return (
                    <>
                        <div>
                            <Typography fontSize={14} fontWeight={400}>
                                {description}
                            </Typography>
                            {examples && (
                                <Typography fontSize={12} fontWeight={300}>
                                    {examples}
                                </Typography>
                            )}
                        </div>
                    </>
                );
            },
            valueGetter: (params) => {return {description: params.row.description, examples: params.row.examples} }, 
        },
        { field: 'bookmark', type: 'boolean', minWidth: 30, width: 30, maxWidth: 30, padding: 0, 
            renderCell: (params) => {
                const {id, path, isLeafNode} = params.row;
                var selected = params.value;
                const onClick = (e) => {
                    e.stopPropagation();
                    selected = !selected;
                    setRows(rows.map(row => row.id === id ? {...row, bookmark: selected} : row));
                    if (selected) {
                        setBookmarkedItems((prev) => ({...prev, [path]: selected}));
                    } else {
                        setBookmarkedItems((prev) => Object.keys(prev).filter((objKey)=>objKey != path).reduce((newObj, key) =>
                            {
                                newObj[key] = prev[key];
                                return newObj;
                            }, {}
                        ));
                    }
                    enqueueSnackbar((selected ? 'Tag bookmarked' : 'Tag bookmark removed'), { variant: 'success' });
                }
                return (isLeafNode) ? ( selected ? (
                    <Bookmark style={{color:'007AFF', padding:'0px'}} onClick={onClick}/>
                  ) : (
                    <BookmarkBorderOutlined style={{color:'007AFF', padding:'0px'}} onClick={onClick}/>
                )) : <></>;
            },
        },
        { field: 'copypaste', headerName: 'Paste', minWidth: 30, width: 30, maxWidth: 30, padding: 0, renderCell: ({value}) => {
            const {isLeafNode, code} = value;
            const onClick = (e) => {
                e.stopPropagation();
                handleCopyTagTemplate(code);
            }
            return (
                <>
                    { isLeafNode ? 
                        <ContentCopyIcon style={{ padding:'0px', cursor: 'pointer', transform: 'scale(1.2)' }} onClick={onClick}/>
                    : (<></>)
                    }
                </>
            );
            }, 
            valueGetter: (params) => { return { isLeafNode: params.row.isLeafNode, code: params.row.code, copyTag: handleCopyTagTemplate }; } 
        },
    ];

    const groupingColDef = {
        width: isSmallScreen ? 180 : 400,
        whiteSpace: 'normal',
        lineHeight: 'normal'
    }

    const [showBookmarked, setShowBookmarked] = React.useState(false);

    const toggleBookmarks = () => {
        setFilterModel((prev) => {
            var newFilter = {...prev};
            if (showBookmarked) {
                newFilter.items = [];
            } else {
                newFilter.items[0] = {
                    id: 1,
                    field: 'bookmark', 
                    operator: 'is', 
                    value: 'true',
                };
            }
            return newFilter;
        });
        setShowBookmarked(!showBookmarked);
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <Grid container alignItems='center'>
                    <Grid sx={{padding: '5px 10px 0px'}}>
                        <GridToolbarQuickFilter/>
                    </Grid>
                    <Grid sx={{right: {marginLeft: 'auto'}, padding: '5px 10px 0px'}}>
                        <Button onClick={toggleBookmarks} size='small' variant='outlined'><Bookmark style={{color:'007AFF'}} />{showBookmarked ? 'Show All' : 'Bookmarks'}</Button>
                        <IconButton
                            onClick={toggleTagDictionary}
                            size="small"
                            aria-label="close"
                            sx={{
                                maxWidth: 30,
                                minWidth: 30,
                                paddingLeft: '10px',
                                color: 'rgba(0, 0, 0, 0.87)',
                                '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.87)' },
                            }}
                        >
                            <Close sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                    </Grid>
                </Grid>
            </GridToolbarContainer>
        );
    }

    if (loading) {
        return <Typography>Loading...</Typography>;
    } else {
        return (
            <>
                <div style={{ flex: 1, width: '100%', height: '100%', minHeight: 0 }}>
                    <DataGridPro
                        treeData
                        filterDebounceMs={1000}
                        rows={rows} 
                        columns={columns} 
                        getTreeDataPath={getTreeDataPath}
                        slots={{ columnHeaders: NoColumnHeaders, toolbar: CustomToolbar }}
                        groupingColDef={groupingColDef}
                        defaultGroupingExpansionDepth={1}
                        localeText={{toolbarFilters: 'Bookmarks'}}
                        hideFooter
                        getRowHeight={() => 'auto'}
                        filterModel={filterModel}
                        onFilterModelChange={setFilterModel}
                        initialState={{
                            density: 'compact',
                            columns:{
                                columnVisibilityModel: {
                                    description: false,
                                    examples: false,
                                }
                            }
                        }}
                        sx={{
                            '&.MuiDataGrid-root .MuiDataGrid-cell': {
                                whiteSpace: 'normal !important',
                                wordWrap: 'break-word !important',
                                padding: '5px',
                            },
                            '& .MuiDataGrid-treeDataGroupingCell': {
                                alignItems: 'flex-start',
                            },
                            '& .MuiDataGrid-treeDataGroupingCell > span': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                minWidth: 0,
                            },
                        }}
                    />
                </div>
            </>
        );
    }

}
